import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  signal,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IonicModule } from '@ionic/angular';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, map, of, switchMap, tap } from 'rxjs';
import {
  CitySuggestion,
  CoordinatesModel,
  GeocodingService,
  knownCitiesConst,
  KnownCity,
  MODAL_TOKEN,
  ModalProvider,
  SelectedLocation
} from '@core';
import { InputWithIconComponent } from '../input-with-icon/input-with-icon.component';
import { OrIconComponent } from '../or-icon/or-icon.component';

export type LocationPickerResult = { kind: 'select'; location: SelectedLocation } | { kind: 'auto' };

@Component({
  selector: 'lib-location-picker-modal',
  templateUrl: './location-picker-modal.component.html',
  styles: [
    `
      .location-divider {
        width: 100%;
        height: 1px;
        background-color: #dadada;
      }

      .sticky-top {
        position: sticky;
        top: 0;
        z-index: 2;
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [IonicModule, ReactiveFormsModule, InputWithIconComponent, OrIconComponent]
})
export class LocationPickerModalComponent implements OnInit {
  @Input() selected: SelectedLocation | null = null;

  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly geocodingService: GeocodingService = inject(GeocodingService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  readonly searchControl: FormControl<string> = new FormControl<string>('', { nonNullable: true });
  readonly popularCities: readonly KnownCity[] = knownCitiesConst;

  readonly query: WritableSignal<string> = signal<string>('');
  readonly results: WritableSignal<CitySuggestion[]> = signal<CitySuggestion[]>([]);
  readonly isSearching: WritableSignal<boolean> = signal<boolean>(false);

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(
        map((value: string) => value.trim()),
        tap((value: string) => {
          this.query.set(value);
          this.isSearching.set(!!value);
        }),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((value: string) => (value ? this.geocodingService.searchCities(value) : of([]))),
        tap(() => this.isSearching.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((results: CitySuggestion[]) => this.results.set(results));
  }

  selectedSourceLabel(): string {
    return this.selected?.source === 'auto' ? 'GPS' : 'Wybrano ręcznie';
  }

  close(): void {
    this.modalProvider.dismissModal$(null);
  }

  locateAutomatically(): void {
    this.dismiss({ kind: 'auto' });
  }

  selectKnownCity(city: KnownCity): void {
    this.dismiss({
      kind: 'select',
      location: { name: city.name, coordinates: city.coordinates, source: 'manual' }
    });
  }

  selectSuggestion(suggestion: CitySuggestion): void {
    this.geocodingService
      .resolveCoordinates(suggestion.placeId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((coordinates: CoordinatesModel | null) => {
        if (!coordinates) return;

        this.dismiss({
          kind: 'select',
          location: { name: suggestion.name, coordinates, source: 'manual' }
        });
      });
  }

  private dismiss(result: LocationPickerResult): void {
    this.modalProvider.dismissModal$(result);
  }
}
